import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';
import type { FormProps } from 'tdesign-react';
import { Button, DialogPlugin, Form, MessagePlugin } from 'tdesign-react';
import {
  ruleChineseEnglishOnly,
  ruleNoChinese,
  ruleLength,
  ruleRequired,
} from '@ssv-website/hooks';
import { LanguageSwitchButton } from '../../language-switch-button';
import { Logo } from '../../logo';
import type { Locale } from '../i18n/config';
import { getUploadPreviewUrl } from './AvatarUploadField';
import {
  COPYRIGHT_TEXT,
  HERO_SUBTITLE,
  HERO_TITLE,
  MEETING_DATE_TEXT,
  MEETING_DESCRIPTION,
  MEETING_LOCATION_TEXT,
  MEETING_TITLE,
  PHONE_DISABLED_PLACEHOLDER,
} from './config';
import desktopHeroIllustration from './hero-right-illustration.png';
import {
  ContactPermissionField,
  DesktopAvatarField,
  DesktopBioFields,
  DesktopInputField,
  TitleListFields,
  emailRules,
  phoneRules,
} from './form-fields';
import { createDefaultFormValues } from './form-helpers';
import { CalendarIcon, LanguageIcon, LocationIcon } from './icons';
import { MeetingInfo, MeetingUserDropdown } from './shared';
import type { SignupFormValues } from './types';

const SUBMITTED_EMPTY_TEXT = '-';

const getSubmittedText = (value: unknown) => {
  const text = String(value ?? '').trim();
  return text || SUBMITTED_EMPTY_TEXT;
};

const formatSubmittedPhone = (value: string) => {
  const text = value.trim();
  if (!text) {
    return SUBMITTED_EMPTY_TEXT;
  }

  const digits = text.replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
  }

  return text;
};

function SubmittedStatusIcon() {
  return (
    <span className="inline-flex h-16 w-16 items-center justify-center text-[#2BA471]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
      >
        <path
          d="M18 32.826L28 42.828L46 24.83L43.17 22L28 37.172L20.826 30L18 32.826Z"
          fill="#2BA471"
        />
        <path
          d="M16.444 8.71885C21.0486 5.64217 26.4621 4 32 4C39.4261 4 46.548 6.94999 51.799 12.201C57.05 17.452 60 24.5739 60 32C60 37.5379 58.3578 42.9514 55.2812 47.556C52.2045 52.1605 47.8315 55.7494 42.7151 57.8686C37.5988 59.9879 31.969 60.5424 26.5375 59.462C21.106 58.3816 16.1169 55.7149 12.201 51.799C8.28515 47.8831 5.61841 42.894 4.53802 37.4625C3.45764 32.0311 4.01213 26.4012 6.13139 21.2849C8.25064 16.1685 11.8395 11.7955 16.444 8.71885ZM18.6663 51.9553C22.6131 54.5924 27.2533 56 32 56C38.3652 56 44.4697 53.4714 48.9706 48.9706C53.4714 44.4697 56 38.3652 56 32C56 27.2532 54.5924 22.6131 51.9553 18.6663C49.3181 14.7195 45.5698 11.6434 41.1844 9.82689C36.799 8.01039 31.9734 7.53511 27.3178 8.46115C22.6623 9.3872 18.3859 11.673 15.0294 15.0294C11.673 18.3859 9.38721 22.6623 8.46117 27.3178C7.53512 31.9734 8.0104 36.799 9.8269 41.1844C11.6434 45.5698 14.7195 49.3181 18.6663 51.9553Z"
          fill="#2BA471"
        />
      </svg>
    </span>
  );
}

// 添加 break-words 以兼容纯英文长单词换行
const DEFAULT_VALUE_CLASS_NAME =
  'text-base leading-6 text-[rgba(0,0,0,0.9)] break-words';

function SubmittedFieldBlock({
  label,
  children,
  widthClass = 'w-full',
  valueClassName = DEFAULT_VALUE_CLASS_NAME,
}: {
  label: string;
  children: ReactNode;
  widthClass?: string;
  valueClassName?: string;
}) {
  return (
    <div className={`${widthClass} flex flex-col gap-2`}>
      <span className="text-sm leading-[22px] text-[rgba(0,0,0,0.4)]">
        {label}
      </span>
      <div className={valueClassName}>{children}</div>
    </div>
  );
}

function DesktopSubmittedDetail({
  values,
  showSuccessHeader = false,
  submitSuccessTitle = '提交成功',
}: {
  values: SignupFormValues;
  showSuccessHeader?: boolean;
  submitSuccessTitle?: string;
}) {
  const avatarUrl = getUploadPreviewUrl(values.avatar);
  const titleItems = values.titles.filter(
    (item) => item.titleCn.trim() || item.titleEn.trim()
  );

  return (
    <>
      {showSuccessHeader && (
        <div className="flex flex-col items-center gap-6">
          <SubmittedStatusIcon />
          <h1 className="text-[20px] font-semibold leading-[28px] text-[rgba(0,0,0,0.9)]">
            {submitSuccessTitle}
          </h1>
        </div>
      )}

      <div
        className={`flex flex-col gap-8 pb-6 ${
          showSuccessHeader ? 'mt-[72px]' : 'pt-10'
        }`}
      >
        <h2 className="text-[20px] leading-[28px] text-[rgba(0,0,0,0.9)]">
          基本信息
        </h2>

        <div className="flex flex-col gap-8">
          <SubmittedFieldBlock label="头像" widthClass="w-[120px]">
            <div className="h-[120px] w-[120px] rounded-[3px] border border-dashed border-[#DCDCDC] bg-[#EEEEEE] p-2">
              {avatarUrl ? (
                <div
                  className="h-[104px] w-[104px] rounded-[3px] bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${avatarUrl})` }}
                />
              ) : null}
            </div>
          </SubmittedFieldBlock>

          {/* <div className="flex gap-8"> */}
          <SubmittedFieldBlock label="姓名" widthClass="w-[400px]">
            {getSubmittedText(values.nameCn)}
          </SubmittedFieldBlock>
          <SubmittedFieldBlock
            label="英文姓名"
            widthClass="w-[400px]"
            // 添加 break-words 以兼容纯英文长单词换行
            valueClassName="text-sm leading-[22px] text-[rgba(0,0,0,0.9)] break-words"
          >
            {getSubmittedText(values.nameEn)}
          </SubmittedFieldBlock>
          {/* </div> */}

          <SubmittedFieldBlock
            label="头衔"
            // 添加 break-words 以兼容纯英文长单词换行
            valueClassName="text-[rgba(0,0,0,0.9)] break-words"
          >
            {titleItems.length ? (
              <div className="flex flex-col gap-1">
                {titleItems.map((item, index) => (
                  <div
                    key={`${item.titleCn}-${item.titleEn}-${index}`}
                    className="flex flex-wrap gap-x-6 gap-y-1"
                  >
                    <span className="text-base leading-6 text-[rgba(0,0,0,0.9)] break-words">
                      {getSubmittedText(item.titleCn)}
                    </span>
                    <span className="text-sm leading-[22px] text-[rgba(0,0,0,0.9)] break-words">
                      {getSubmittedText(item.titleEn)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              SUBMITTED_EMPTY_TEXT
            )}
          </SubmittedFieldBlock>

          {/* <SubmittedFieldBlock label="国籍">
            {getSubmittedText(values.nationality)}
          </SubmittedFieldBlock> */}
          <SubmittedFieldBlock label="电话">
            {formatSubmittedPhone(values.phone)}
          </SubmittedFieldBlock>
          <SubmittedFieldBlock label="邮箱">
            {getSubmittedText(values.email)}
          </SubmittedFieldBlock>
          <SubmittedFieldBlock
            label="个人简介"
            // 添加 break-words 以兼容纯英文长单词换行
            valueClassName="text-[rgba(0,0,0,0.9)] break-words"
          >
            {/* <div className="grid grid-cols-2 gap-6"> */}
            <div className="text-base leading-6 text-[rgba(0,0,0,0.9)] break-words">
              {getSubmittedText(values.bioCn)}
            </div>
            <div className="text-base leading-6 text-[rgba(0,0,0,0.9)] mt-2 break-words">
              {getSubmittedText(values.bioEn)}
            </div>
            {/* </div> */}
          </SubmittedFieldBlock>

          <SubmittedFieldBlock label="是否允许通过站内信联系">
            {values.allowContact === 0 ? '拒绝' : '允许'}
          </SubmittedFieldBlock>
        </div>
      </div>
    </>
  );
}

type DesktopSignupPageProps = {
  displayedPhone: string;
  enableLanguageSwitch: boolean;
  form: any;
  hasTeam: boolean;
  heroBackgroundSrc: string;
  isReadonlySubmittedState: boolean;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  isSubmitSuccessState?: boolean; // 是否是从编辑/新增成功跳转过来的状态
  langLabel: string;
  onCancel: () => void;
  onPrimaryAction: () => void;
  onSubmit: FormProps['onSubmit'];
  readonlyValues: SignupFormValues;
  submitButtonText: string;
  submitSuccessTitle?: string;
  syncSubmitState: (...args: any[]) => void;
  targetLang: Locale;
  teamType?: number;
};

export function DesktopSignupPage({
  displayedPhone,
  enableLanguageSwitch,
  form,
  hasTeam,
  heroBackgroundSrc,
  isReadonlySubmittedState,
  isSubmitDisabled,
  isSubmitting,
  isSubmitSuccessState = false,
  langLabel,
  onCancel,
  onPrimaryAction,
  onSubmit,
  readonlyValues,
  submitButtonText,
  submitSuccessTitle,
  syncSubmitState,
  targetLang,
  teamType,
}: DesktopSignupPageProps) {
  const handleCancel = () => {
    const confirmDialog = DialogPlugin.confirm({
      header: '提示',
      body: '确定要取消提交吗？已填写的内容将不会保存',
      confirmBtn: '确定',
      cancelBtn: '取消',
      onConfirm: () => {
        confirmDialog.hide();
        onCancel();
      },
      onCancel: () => {
        confirmDialog.hide();
      },
      onClose: () => {
        confirmDialog.hide();
      },
    });
  };

  return (
    <div className="signup-2026-desktop min-h-screen bg-[#F5F7FA] text-[#121212]">
      <section
        className="relative h-[320px] overflow-hidden bg-cover bg-center bg-no-repeat "
        style={{ backgroundImage: `url(${heroBackgroundSrc})` }}
      >
        <header className="fixed inset-x-0 top-0 z-30 h-14 border-b border-black/10 bg-white/60 backdrop-blur-[20px]">
          <div className="flex h-full items-center justify-between px-10">
            <Link href="/" className="inline-flex items-center">
              <Logo
                type="white"
                className="h-[29px] w-[120.5px] object-contain"
              />
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitchButton
                targetLang={targetLang}
                className="inline-flex items-center gap-1 rounded-full border border-[#2C2C2C] bg-white/50 px-3 py-[5px] text-sm leading-[22px] text-[#2C2C2C]"
                onClick={(event) => {
                  if (enableLanguageSwitch) return;
                  event.preventDefault();
                  MessagePlugin.warning('英文页面待补充');
                }}
              >
                <LanguageIcon />
                <span>{langLabel}</span>
              </LanguageSwitchButton>
              <MeetingUserDropdown
                displayedPhone={displayedPhone}
                hasTeam={hasTeam}
                teamType={teamType}
              />
            </div>
          </div>
        </header>

        <div className="relative mx-auto flex h-full w-[833px] items-center pt-14">
          <div className="flex shrink-0 flex-col gap-9">
            <div className="flex flex-col gap-2">
              <p className="text-[48px] font-semibold leading-[56px] text-[#003CAB]">
                {HERO_TITLE}
              </p>
              <p className="text-[24px] font-semibold leading-8 text-[#003CAB]">
                {HERO_SUBTITLE}
              </p>
            </div>
            <div className="flex items-center gap-8 text-sm leading-[22px] text-[#121212]">
              <MeetingInfo
                className="gap-3"
                icon={
                  <span className="text-[#B5C7FF]">
                    <CalendarIcon />
                  </span>
                }
              >
                {MEETING_DATE_TEXT}
              </MeetingInfo>
              <MeetingInfo
                className="gap-3"
                icon={
                  <span className="text-[#B5C7FF]">
                    <LocationIcon />
                  </span>
                }
              >
                {MEETING_LOCATION_TEXT}
              </MeetingInfo>
            </div>
          </div>
          <Image
            src={desktopHeroIllustration}
            alt="SSV 2026 hero illustration"
            priority
            sizes="253px"
            className="relative left-[217px] [278px] w-[253px] shrink-0 object-contain"
          />
        </div>
      </section>

      <main className="bg-white">
        <div className="mx-auto w-[833px]">
          {isReadonlySubmittedState ? (
            <div className="mx-auto flex w-[832px] flex-col pt-10">
              <DesktopSubmittedDetail
                values={readonlyValues}
                showSuccessHeader={isSubmitSuccessState}
                submitSuccessTitle={submitSuccessTitle}
              />
            </div>
          ) : (
            <div className="mx-auto flex w-[833px] flex-col gap-10 pt-[50px]">
              <div className="flex flex-col gap-6">
                <h1 className="text-[20px] font-semibold leading-[28px] text-[rgba(0,0,0,0.9)]">
                  {MEETING_TITLE}
                </h1>
                <p className="text-base leading-[24px] text-[rgba(0,0,0,0.9)]">
                  {MEETING_DESCRIPTION}
                </p>
              </div>

              <div className="flex flex-col gap-6">
                <h2 className="text-[20px] leading-[28px] text-[rgba(0,0,0,0.9)]">
                  基础信息
                </h2>

                <Form
                  className="flex flex-col"
                  form={form}
                  initialData={readonlyValues}
                  labelAlign="top"
                  layout="vertical"
                  preventSubmitDefault
                  requiredMark={false}
                  scrollToFirstError="smooth"
                  showErrorMessage
                  statusIcon={false}
                  submitWithWarningMessage={false}
                  onSubmit={onSubmit}
                  onValuesChange={syncSubmitState}
                >
                  <DesktopAvatarField
                    onUploadStateChange={syncSubmitState}
                    disabled={isReadonlySubmittedState}
                  />

                  <div className="flex gap-8 pb-6">
                    <div className="w-[400px]">
                      <DesktopInputField
                        label="中文姓名"
                        name="nameCn"
                        placeholder="请输入中文姓名"
                        required
                        rules={[
                          ruleRequired({ text: '中文姓名' }),
                          ruleChineseEnglishOnly,
                          ruleLength(20),
                        ]}
                        maxlength={20}
                        disabled={isReadonlySubmittedState}
                      />
                    </div>
                    <div className="w-[400px]">
                      <DesktopInputField
                        label="英文姓名"
                        name="nameEn"
                        placeholder="请输入英文姓名"
                        required
                        rules={[
                          ruleRequired({ text: '英文姓名' }),
                          ruleNoChinese,
                          ruleLength(50),
                        ]}
                        maxlength={50}
                        disabled={isReadonlySubmittedState}
                      />
                    </div>
                  </div>

                  <TitleListFields
                    form={form}
                    disabled={isReadonlySubmittedState}
                  />

                  {/* <div className="flex gap-8 pb-6">
                    <div className="w-[400px]">
                      <DesktopInputField
                        label="国籍"
                        name="nationality"
                        placeholder="请输入国籍"
                        required
                        rules={[ruleRequired({ text: '国籍' })]}
                        disabled={isReadonlySubmittedState}
                      />
                    </div>
                  </div> */}

                  <div className="flex gap-8 pb-6">
                    <div className="w-[400px]">
                      <DesktopInputField
                        label="电话"
                        name="phone"
                        placeholder={PHONE_DISABLED_PLACEHOLDER}
                        required
                        rules={phoneRules}
                        maxlength={20}
                        disabled
                      />
                    </div>
                    <div className="w-[400px]">
                      <DesktopInputField
                        label="邮箱"
                        name="email"
                        placeholder="请输入邮箱地址"
                        required
                        rules={emailRules}
                        maxlength={100}
                        disabled={isReadonlySubmittedState}
                      />
                    </div>
                  </div>

                  <DesktopBioFields disabled={isReadonlySubmittedState} />

                  <div className="pb-6">
                    <ContactPermissionField
                      desktop
                      disabled={isReadonlySubmittedState}
                    />
                  </div>
                </Form>
              </div>
            </div>
          )}
        </div>

        <footer className="mt-8 pb-4 pt-2">
          <div className="flex flex-col gap-8">
            <div className="rounded-b-[6px] bg-[#F3F3F3] px-[164px] py-8">
              <div className="mx-auto flex w-[832px] items-center justify-center gap-2">
                <Button
                  size="medium"
                  theme="primary"
                  variant="base"
                  className="!h-8 !rounded-[3px] !px-4 !text-sm !font-normal !leading-[22px]"
                  loading={isSubmitting}
                  disabled={isSubmitDisabled}
                  onClick={onPrimaryAction}
                >
                  {submitButtonText}
                </Button>
                <Button
                  size="medium"
                  theme="default"
                  variant="base"
                  className={`!h-8 !rounded-[3px] !border-transparent !bg-[#E7E7E7] !px-4 !text-sm !font-normal !leading-[22px] !text-[rgba(0,0,0,0.9)] hover:!bg-[#DCDCDC] ${
                    isReadonlySubmittedState ? '!hidden' : ''
                  }`}
                  disabled={isSubmitting}
                  onClick={handleCancel}
                >
                  取消
                </Button>
              </div>
            </div>
            <p className="text-center text-sm leading-[22px] text-[rgba(0,0,0,0.4)]">
              {COPYRIGHT_TEXT}
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
